import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import ToolPageLayout from "@/components/tools/ToolPageLayout";
import { getRelatedTools } from "@/lib/toolsData";

const ConversionCalculator = () => {
  const [conversions, setConversions] = useState<string>("");
  const [visitors, setVisitors] = useState<string>("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const conversionsNum = parseFloat(conversions) || 0;
    const visitorsNum = parseFloat(visitors) || 0;
    
    if (visitorsNum === 0) {
      setResult(null);
      return;
    }

    const rate = (conversionsNum / visitorsNum) * 100;
    setResult(rate);
  };

  const reset = () => {
    setConversions("");
    setVisitors("");
    setResult(null);
  };

  const getAssessment = (rate: number) => {
    if (rate >= 5) return { label: "Excellent", color: "text-green-600" };
    if (rate >= 3) return { label: "Good", color: "text-green-500" };
    if (rate >= 2) return { label: "Average", color: "text-yellow-600" };
    if (rate >= 1) return { label: "Below Average", color: "text-orange-500" };
    return { label: "Needs Improvement", color: "text-red-600" };
  };

  const relatedTools = getRelatedTools("conversion-calculator");

  return (
    <ToolPageLayout
      title="Conversion Rate Calculator"
      description="Calculate your website or campaign conversion rate to measure how effectively you turn visitors into customers."
      metaDescription="Conversion rate measures the percentage of visitors who complete a desired action. It's the most important metric for understanding funnel efficiency and optimizing marketing ROI."
      icon={Calculator}
      slug="conversion-calculator"
      formula={{
        equation: "Conversion Rate = (Conversions / Total Visitors) × 100",
        variables: [
          { name: "Conversions", description: "Number of visitors who completed the desired action" },
          { name: "Total Visitors", description: "Total number of visitors to your page or site" },
        ]
      }}
      example={{
        inputs: [
          { label: "Conversions", value: "150" },
          { label: "Total Visitors", value: "5,000" },
        ],
        output: "3.00% Conversion Rate",
        interpretation: "3% of your visitors are converting. This is a solid rate for e-commerce, though benchmarks vary by industry."
      }}
      improvements={[
        "Simplify forms and reduce friction in the conversion process",
        "Improve page load speed to reduce bounce rates",
        "Add social proof like testimonials and reviews",
        "Create clear, compelling calls-to-action",
        "A/B test headlines, images, and page layouts",
        "Ensure mobile experience is optimized",
      ]}
      relatedTools={relatedTools.map(t => ({ title: t.title, href: t.href, description: t.description }))}
      relatedService={{ title: "CRO Services", href: "/cro" }}
    >
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Calculate Conversion Rate</CardTitle>
          <CardDescription>Enter your conversions and visitors</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="conversions">Total Conversions</Label>
            <Input
              id="conversions"
              type="number"
              placeholder="150"
              value={conversions}
              onChange={(e) => setConversions(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="visitors">Total Visitors</Label>
            <Input
              id="visitors"
              type="number"
              placeholder="5000"
              value={visitors}
              onChange={(e) => setVisitors(e.target.value)}
            />
          </div>

          <div className="flex gap-3">
            <Button onClick={calculate} className="flex-1">
              Calculate Rate
            </Button>
            <Button variant="outline" onClick={reset}>
              Reset
            </Button>
          </div>

          {result !== null && (
            <div className="mt-6 p-6 bg-primary/5 rounded-lg border border-primary/20">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Conversion Rate</p>
                <p className={`text-4xl font-bold ${getAssessment(result).color}`}>
                  {result.toFixed(2)}%
                </p>
                <p className={`text-sm mt-3 ${getAssessment(result).color}`}>
                  {getAssessment(result).label}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </ToolPageLayout>
  );
};

export default ConversionCalculator;
